import { useMemo } from 'react';
import {
  Card, CardContent, Grid, Typography, Box, Stack, Divider, Chip, LinearProgress, Table, TableHead, TableRow, TableCell, TableBody, MenuItem, TextField,
} from '@mui/material';
import { ShoppingCart, AccountTree, AccountBalanceWallet, EngineeringOutlined, Storefront, Groups } from '@mui/icons-material';
import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { PageHeader } from '../../components/PageHeader';
import { ListFilters, EMPTY_FILTERS, toDashboardParams, type FilterValue } from '../../components/ListFilters';
import { pdiDashboardApi } from '../../api/pdiDashboard.api';
import { dealerInsightsApi } from '../../api/dealerInsights.api';
import { verticalsApi } from '../../api/verticals.api';
import { useVertical } from '../../hooks/useVertical';
import { useAuth } from '../../hooks/useAuth';

const inr = (n = 0) => `₹${Number(n).toLocaleString('en-IN', { maximumFractionDigits: 0 })}`;
const now = new Date().getFullYear();
const YEARS = [now - 1, now, now + 1];

function Bar({ label, value, max, hint, color = 'primary.main' }: { label: string; value: number; max: number; hint?: string; color?: string }) {
  return (
    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
      <Typography variant="caption" sx={{ width: 130, flexShrink: 0, color: 'text.secondary', textTransform: 'capitalize' }}>{label.replace(/_/g, ' ')}</Typography>
      <Box sx={{ flexGrow: 1, bgcolor: 'action.hover', borderRadius: 1, height: 18 }}>
        <Box sx={{ width: `${Math.round((value / Math.max(1, max)) * 100)}%`, bgcolor: color, height: '100%', borderRadius: 1, minWidth: value ? 6 : 0 }} />
      </Box>
      <Typography variant="caption" sx={{ width: 110, textAlign: 'right' }}>{hint ?? value}</Typography>
    </Box>
  );
}

function Panel({ title, icon, children }: { title: string; icon: React.ReactNode; children: React.ReactNode }) {
  return (
    <Card variant="outlined" sx={{ height: '100%' }}><CardContent>
      <Typography variant="subtitle1" gutterBottom>{icon}<Box component="span" sx={{ ml: 0.5, verticalAlign: 'middle' }}>{title}</Box></Typography>
      <Divider sx={{ mb: 1.5 }} />
      {children}
    </CardContent></Card>
  );
}

/**
 * Deep Analytics — the Business Head's detailed drill-down: order status & trend,
 * fulfilment pipeline, finance ageing & collection, installation SLA, dealer-wise
 * target vs achievement, and team performance. Composes existing scope-aware APIs.
 */
export function DeepAnalyticsPage() {
  const { verticals } = useVertical();
  const { user } = useAuth();
  // Dealer/Client portal users don't see internal dealer-target & team-performance analytics.
  const isPortal = user?.linkedEntityType === 'dealer' || user?.linkedEntityType === 'client';
  const [year, setYear] = useState(now);
  const [filters, setFilters] = useState<FilterValue>({ ...EMPTY_FILTERS });
  const pdiVid = verticals.find((v) => v.code === 'protection_device_installation')?.id;
  const fp = toDashboardParams(filters, year);

  const dash = useQuery({ queryKey: ['pdi-dashboard', fp], queryFn: () => pdiDashboardApi.overview(fp) });
  const dealerAn = useQuery({ queryKey: ['dealer-analytics', year], queryFn: () => dealerInsightsApi.analytics(year), enabled: !isPortal });
  const team = useQuery({ queryKey: ['team-performance', pdiVid, year], queryFn: () => verticalsApi.teamPerformance({ verticalId: pdiVid, year }), enabled: Boolean(pdiVid) && !isPortal });

  const d = dash.data;
  const orderStatusMax = useMemo(() => Math.max(1, ...Object.values(d?.orders.byStatus ?? {})), [d]);
  const trendMax = useMemo(() => Math.max(1, ...(d?.orders.monthlyTrend ?? []).map((t) => t.count)), [d]);
  const ageingMax = useMemo(() => Math.max(1, ...(d?.finance.ageing ?? []).map((a) => a.value)), [d]);
  const collectionPct = d && d.finance.totalInvoiceValue > 0 ? Math.round((d.finance.paidAmount / d.finance.totalInvoiceValue) * 100) : 0;

  const FUNNEL = ['approved', 'in_manufacturing', 'manufactured', 'allocated', 'dispatched', 'delivered'];

  return (
    <>
      <PageHeader title="Deep Analytics" subtitle="Detailed PDI business analytics — orders, fulfilment, finance, installations, dealers & team." />
      <Stack direction="row" spacing={1.5} sx={{ mb: 1 }} alignItems="center" flexWrap="wrap" useFlexGap>
        <TextField select size="small" label="Year" value={year} onChange={(e) => setYear(Number(e.target.value))} sx={{ width: 120 }}>
          {YEARS.map((y) => <MenuItem key={y} value={y}>{y}</MenuItem>)}
        </TextField>
      </Stack>
      <ListFilters value={filters} onChange={setFilters}
        statusOptions={['draft', 'pending_approval', 'approved', 'in_manufacturing', 'manufactured', 'allocated', 'dispatched', 'delivered', 'rejected', 'cancelled']}
        show={['status', 'month', 'dateFrom', 'dateTo', 'region', 'dealer']} />
      {(dash.isLoading || team.isLoading) && <LinearProgress sx={{ mb: 2 }} />}

      {d && (
        <Grid container spacing={2}>
          <Grid item xs={12} md={6}>
            <Panel title="Order Status Breakdown" icon={<ShoppingCart fontSize="small" sx={{ verticalAlign: 'middle' }} />}>
              <Stack spacing={0.75}>
                {Object.entries(d.orders.byStatus).length === 0 && <Typography variant="caption" color="text.secondary">No orders.</Typography>}
                {Object.entries(d.orders.byStatus).map(([s, c]) => <Bar key={s} label={s} value={c} max={orderStatusMax} />)}
              </Stack>
            </Panel>
          </Grid>

          <Grid item xs={12} md={6}>
            <Panel title="Fulfilment Pipeline" icon={<AccountTree fontSize="small" sx={{ verticalAlign: 'middle' }} />}>
              <Stack spacing={0.75}>
                {FUNNEL.map((s) => <Bar key={s} label={s} value={d.orders.byStatus[s] ?? 0} max={orderStatusMax} color="#d97706" />)}
              </Stack>
            </Panel>
          </Grid>

          <Grid item xs={12} md={7}>
            <Panel title="Monthly Order Trend" icon={<ShoppingCart fontSize="small" sx={{ verticalAlign: 'middle' }} />}>
              <Stack spacing={0.75}>
                {(d.orders.monthlyTrend ?? []).map((t) => <Bar key={t.month} label={t.month} value={t.count} max={trendMax} hint={`${t.count} · ${inr(t.value)}`} />)}
              </Stack>
            </Panel>
          </Grid>

          <Grid item xs={12} md={5}>
            <Panel title="Finance — Ageing & Collection" icon={<AccountBalanceWallet fontSize="small" sx={{ verticalAlign: 'middle' }} />}>
              <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={`Invoiced ${inr(d.finance.totalInvoiceValue)}`} />
                <Chip size="small" color="success" label={`Paid ${inr(d.finance.paidAmount)}`} />
                <Chip size="small" color="warning" label={`Outstanding ${inr(d.finance.outstandingAmount)}`} />
                <Chip size="small" color="error" label={`Overdue ${inr(d.finance.overdueAmount)}`} />
              </Stack>
              <Box sx={{ mb: 1 }}><Typography variant="caption" color="text.secondary">Collection {collectionPct}%</Typography><LinearProgress variant="determinate" value={Math.min(100, collectionPct)} sx={{ height: 8, borderRadius: 1 }} /></Box>
              <Stack spacing={0.75}>
                {(d.finance.ageing ?? []).map((a) => <Bar key={a.label} label={a.label} value={a.value} max={ageingMax} hint={inr(a.value)} color="#dc2626" />)}
              </Stack>
            </Panel>
          </Grid>

          <Grid item xs={12} md={5}>
            <Panel title="Installations & SLA" icon={<EngineeringOutlined fontSize="small" sx={{ verticalAlign: 'middle' }} />}>
              <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={`Pending ${d.installations.pending}`} />
                <Chip size="small" color="warning" label={`Ongoing ${d.installations.ongoing}`} />
                <Chip size="small" color="success" label={`Completed ${d.installations.completed}`} />
                <Chip size="small" color={d.installations.slaBreached ? 'error' : 'default'} label={`SLA Breached ${d.installations.slaBreached}`} />
              </Stack>
              <Box><Typography variant="caption" color="text.secondary">SLA Performance {d.installations.slaPerformance}%</Typography><LinearProgress variant="determinate" value={Math.min(100, d.installations.slaPerformance)} color={d.installations.slaPerformance >= 90 ? 'success' : 'warning'} sx={{ height: 8, borderRadius: 1 }} /></Box>
            </Panel>
          </Grid>

          {!isPortal && <Grid item xs={12} md={7}>
            <Panel title={`Dealer-wise Target vs Achievement (${year})`} icon={<Storefront fontSize="small" sx={{ verticalAlign: 'middle' }} />}>
              <Table size="small">
                <TableHead><TableRow><TableCell>Dealer</TableCell><TableCell align="right">Target</TableCell><TableCell align="right">Achieved</TableCell><TableCell align="right">%</TableCell></TableRow></TableHead>
                <TableBody>
                  {(dealerAn.data?.rows ?? []).slice(0, 10).map((r) => (
                    <TableRow key={r.dealerId}><TableCell>{r.dealerName}</TableCell><TableCell align="right">{inr(r.target)}</TableCell><TableCell align="right">{inr(r.achieved)}</TableCell>
                      <TableCell align="right"><Chip size="small" color={r.achievementPct >= 100 ? 'success' : r.achievementPct >= 60 ? 'warning' : 'default'} label={`${r.achievementPct}%`} /></TableCell></TableRow>
                  ))}
                  {(dealerAn.data?.rows.length ?? 0) === 0 && <TableRow><TableCell colSpan={4}><Typography variant="caption" color="text.secondary">No dealer targets for {year}.</Typography></TableCell></TableRow>}
                </TableBody>
              </Table>
            </Panel>
          </Grid>}

          {!isPortal && <Grid item xs={12}>
            <Panel title="Team Performance" icon={<Groups fontSize="small" sx={{ verticalAlign: 'middle' }} />}>
              <Stack direction="row" spacing={1} sx={{ mb: 1.5 }} flexWrap="wrap" useFlexGap>
                <Chip size="small" label={`Team ${team.data?.totals.teamSize ?? 0}`} />
                <Chip size="small" label={`Target ${inr(team.data?.totals.target)}`} />
                <Chip size="small" color="success" label={`Achieved ${inr(team.data?.totals.achievedRevenue)}`} />
                <Chip size="small" label={`${team.data?.totals.achievementPct ?? 0}%`} />
              </Stack>
              <Table size="small">
                <TableHead><TableRow><TableCell>Member</TableCell><TableCell align="right">Target</TableCell><TableCell align="right">Achieved</TableCell><TableCell align="right">%</TableCell><TableCell align="right">Work Items</TableCell></TableRow></TableHead>
                <TableBody>
                  {(team.data?.members ?? []).map((m) => (
                    <TableRow key={m.userId}><TableCell>{m.userName}</TableCell><TableCell align="right">{inr(m.target)}</TableCell><TableCell align="right">{inr(m.achievedRevenue)}</TableCell>
                      <TableCell align="right"><Chip size="small" color={m.achievementPct >= 100 ? 'success' : m.achievementPct >= 60 ? 'warning' : 'default'} label={`${m.achievementPct}%`} /></TableCell><TableCell align="right">{m.workItems}</TableCell></TableRow>
                  ))}
                  {(team.data?.members.length ?? 0) === 0 && <TableRow><TableCell colSpan={5}><Typography variant="caption" color="text.secondary">No team members mapped.</Typography></TableCell></TableRow>}
                </TableBody>
              </Table>
            </Panel>
          </Grid>}
        </Grid>
      )}
    </>
  );
}
